import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface NotificationLogEntry {
  id: string;
  template_id: string;
  ticket_id: string;
  event_id: string;
  user_id: string;
  sent_at: string;
  status: string;
  channel: string;
  error_message: string | null;
  template?: {
    name: string;
    hours_before: number;
  };
  event?: {
    title: string;
  };
  profile?: {
    email: string;
    first_name: string;
    last_name: string;
  };
}

export interface ProcessingRunEntry {
  id: string;
  triggered_at: string;
  triggered_by: string;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  details: Record<string, unknown> | null;
}

interface UseNotificationActivityLogOptions {
  limit?: number;
  eventId?: string;
  templateId?: string;
}

export function useNotificationActivityLog(options: UseNotificationActivityLogOptions = {}) {
  const { limit = 50, eventId, templateId } = options;
  const queryClient = useQueryClient();

  const logQuery = useQuery({
    queryKey: ["notification-activity-log", { eventId, templateId, limit }],
    queryFn: async () => {
      let query = supabase
        .from("notification_log")
        .select(`
          *,
          template:notification_templates(name, hours_before),
          event:events(title)
        `)
        .order("sent_at", { ascending: false })
        .limit(limit);

      if (eventId) {
        query = query.eq("event_id", eventId);
      }

      if (templateId) {
        query = query.eq("template_id", templateId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Fetch profiles separately since there's no FK relationship
      const userIds = [...new Set((data || []).map(log => log.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name")
        .in("id", userIds);
      
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
      
      return (data || []).map(log => ({
        ...log,
        profile: profileMap.get(log.user_id) || null,
      })) as NotificationLogEntry[];
    },
  });

  const processingRunsQuery = useQuery({
    queryKey: ["notification-processing-runs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notification_processing_runs")
        .select("*")
        .order("triggered_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      return data as ProcessingRunEntry[];
    },
  });

  const triggerProcessingMutation = useMutation({
    mutationFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error("Not authenticated");
      }

      const response = await supabase.functions.invoke("process-scheduled-notifications", {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`,
        },
        body: { mode: "manual" },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Processing complete: ${data.sent} sent, ${data.failed} failed, ${data.skipped} skipped`);
      // Refetch both queries
      queryClient.invalidateQueries({ queryKey: ["notification-activity-log"] });
      queryClient.invalidateQueries({ queryKey: ["notification-processing-runs"] });
    },
    onError: (error) => {
      toast.error(`Failed to trigger processing: ${error.message}`);
    },
  });

  return {
    logs: logQuery.data ?? [],
    processingRuns: processingRunsQuery.data ?? [],
    isLoading: logQuery.isLoading,
    isLoadingRuns: processingRunsQuery.isLoading,
    error: logQuery.error,
    refetch: logQuery.refetch,
    triggerProcessing: triggerProcessingMutation.mutate,
    isTriggering: triggerProcessingMutation.isPending,
  };
}
